/** EGB-00001 — Enterprise Architecture Governance Framework (mirrors pkg/architecturegovernance) */

export const EGB_ID = 'EGB-00001';

export interface BoardMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface PrincipleRow {
  principle: string;
  status: string;
  enforced: boolean;
}

export interface ADRRow {
  id: string;
  title: string;
  status: string;
  reviewer: string;
}

export interface TechnologyRow {
  domain: string;
  standard: string;
  status: string;
  version: string;
}

export interface DomainIntegrationRow {
  area: string;
  detail: string;
  status: string;
}

export interface DatabaseAIRow {
  domain: string;
  standard: string;
  status: string;
}

export interface PerformanceSecurityUXRow {
  domain: string;
  metric: string;
  target: string;
  status: string;
}

export interface ComplianceEvolutionRow {
  category: string;
  item: string;
  status: string;
  progress: number;
}

export const BOARD_ROLES = [
  'Chief Enterprise Architect', 'CTO', 'AI Architect', 'Security Architect', 'Data Architect',
  'Platform Architect', 'Integration Architect', 'DevOps Architect', 'UX Architect', 'Product Architect',
];

export const BOARD_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Review Cycles', 'Approval Matrix', 'Escalation Paths'];

export const ARCHITECTURE_PRINCIPLES = [
  'Business First', 'AI First', 'Marketplace Agnostic', 'Cloud Ready', 'API First',
  'Event Driven', 'Security by Design', 'Accessibility by Design', 'Performance by Design',
];

export const ADR_CAPABILITIES = ['ADR Templates', 'Decision Logs', 'Alternatives', 'Trade-off Analysis', 'Review Process', 'Historical Tracking'];

export const TECHNOLOGY_DOMAINS = [
  'Programming Languages', 'Frameworks', 'Databases', 'Messaging', 'AI Models',
  'Infrastructure', 'CI/CD', 'Third-party Dependencies', 'Deprecation Policies',
];

export const DOMAIN_GOVERNANCE = ['Business Domains', 'Ownership', 'Bounded Contexts', 'Dependencies', 'Versioning', 'Integration Boundaries'];

export const INTEGRATION_STANDARDS = ['API Contracts', 'Event Contracts', 'Naming Standards', 'Versioning', 'Backward Compatibility', 'Deprecation'];

export const DATABASE_GOVERNANCE = ['Schema Standards', 'Migration Standards', 'Indexing Policies', 'Retention Policies', 'Audit Standards'];

export const AI_GOVERNANCE = ['Prompt Standards', 'DNA Standards', 'Model Lifecycle', 'Explainability', 'Confidence Rules', 'Human Oversight'];

export const PERFORMANCE_GOVERNANCE = ['Performance Budgets', 'Latency Targets', 'Caching Policies', 'Scaling Standards', 'Optimization Guidelines'];

export const SECURITY_GOVERNANCE = ['Authentication', 'Authorization', 'Encryption', 'Secrets Management', 'IAM', 'Threat Modeling'];

export const UX_GOVERNANCE = ['Design System', 'Accessibility', 'Reusable Components', 'Interaction Patterns', 'Consistency Standards'];

export const DOCUMENTATION_GOVERNANCE = ['Architecture Documentation', 'API Documentation', 'Runbooks', 'Diagrams', 'Versioning', 'Ownership'];

export const TECHNICAL_DEBT = ['Debt Register', 'Prioritization', 'Ownership', 'Resolution Plans', 'Risk Assessment'];

export const COMPLIANCE_REVIEW = ['Architecture Reviews', 'Compliance Audits', 'CI Policy Checks', 'Release Gates', 'Exception Management'];

export const FUTURE_ARCHITECTURE = ['Technology Radar', 'Innovation Backlog', 'Architecture Evolution', 'Research Initiatives'];

export const ARCHITECTURE_STAGES = [
  'Governance Board', 'Architecture Principles', 'ADR Governance', 'Technology Governance',
  'Domain & Integration', 'Database & AI', 'Performance · Security · UX', 'Compliance & Evolution',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  governance: '/governance',
  designSystem: '/platform/design-system',
  enterpriseIntegration: '/platform/enterprise-integration',
};

export const DEMO_BOARD: BoardMemberRow[] = [
  { role: 'Chief Enterprise Architect', responsibility: 'Architecture standards & governance', decisionRights: 'Final architecture approval', reviewCycle: 'Weekly' },
  { role: 'CTO', responsibility: 'Technology strategy & investment', decisionRights: 'Executive escalation', reviewCycle: 'Monthly' },
  { role: 'AI Architect', responsibility: 'AI capabilities & model governance', decisionRights: 'AI architecture approval', reviewCycle: 'Bi-weekly' },
  { role: 'Security Architect', responsibility: 'Security architecture & threat modeling', decisionRights: 'Security gate veto', reviewCycle: 'Weekly' },
  { role: 'Platform Architect', responsibility: 'Platform kernel & shared services', decisionRights: 'Platform change approval', reviewCycle: 'Weekly' },
  { role: 'Integration Architect', responsibility: 'API & event contract standards', decisionRights: 'Integration pattern approval', reviewCycle: 'Bi-weekly' },
];

export const DEMO_PRINCIPLES: PrincipleRow[] = [
  { principle: 'Business First', status: 'enforced', enforced: true },
  { principle: 'AI First', status: 'enforced', enforced: true },
  { principle: 'Marketplace Agnostic', status: 'enforced', enforced: true },
  { principle: 'Event Driven', status: 'enforced', enforced: true },
  { principle: 'Security by Design', status: 'enforced', enforced: true },
  { principle: 'Accessibility by Design', status: 'enforced', enforced: true },
];

export const DEMO_ADRS: ADRRow[] = [
  { id: 'ADR-0001', title: 'Event-Driven Architecture', status: 'accepted', reviewer: 'Chief Enterprise Architect' },
  { id: 'ADR-0042', title: 'Multi-Tenant Isolation Model', status: 'accepted', reviewer: 'Security Architect' },
  { id: 'ADR-0098', title: 'Architecture Review Board Governance', status: 'accepted', reviewer: 'CTO' },
  { id: 'ADR-0142', title: 'AI Decision Engine Gateway', status: 'proposed', reviewer: 'AI Architect' },
];

export const DEMO_TECHNOLOGY: TechnologyRow[] = [
  { domain: 'Programming Languages', standard: 'Go 1.22+ (services), TypeScript (frontend)', status: 'approved', version: 'current' },
  { domain: 'Databases', standard: 'PostgreSQL + Redis', status: 'approved', version: '16.x' },
  { domain: 'Messaging', standard: 'NATS JetStream / outbox pattern', status: 'approved', version: '2.x' },
  { domain: 'AI Models', standard: 'Multi-provider routing with fallback', status: 'approved', version: 'v3.2' },
  { domain: 'CI/CD', standard: 'GitHub Actions + canary deploy', status: 'approved', version: 'current' },
  { domain: 'Deprecation Policies', standard: '90-day notice + migration guide', status: 'enforced', version: 'policy v1' },
];

export const DEMO_DOMAIN_INTEGRATION: DomainIntegrationRow[] = [
  { area: 'Domain', detail: '65 bounded contexts registered', status: 'governed' },
  { area: 'Domain', detail: 'Domain ownership matrix published', status: 'active' },
  { area: 'Integration', detail: 'OpenAPI + event catalog mandatory', status: 'enforced' },
  { area: 'Integration', detail: 'Semantic versioning on all contracts', status: 'enforced' },
  { area: 'Integration', detail: 'Backward compatibility for 2 major versions', status: 'policy' },
];

export const DEMO_DATABASE_AI: DatabaseAIRow[] = [
  { domain: 'Database', standard: 'Tenant-scoped schema + audit columns', status: 'enforced' },
  { domain: 'Database', standard: 'Migration review required for L3+ changes', status: 'enforced' },
  { domain: 'AI', standard: 'Prompt version history permanent', status: 'enforced' },
  { domain: 'AI', standard: 'Confidence threshold ≥ 0.85 for auto-actions', status: 'policy' },
  { domain: 'AI', standard: 'Human override on all L4+ decisions', status: 'enforced' },
];

export const DEMO_PERF_SEC_UX: PerformanceSecurityUXRow[] = [
  { domain: 'Performance', metric: 'API p95 latency', target: '<200ms', status: 'within_budget' },
  { domain: 'Performance', metric: 'Page load LCP', target: '<2.5s', status: 'within_budget' },
  { domain: 'Security', metric: 'Zero Trust + RBAC', target: '100% coverage', status: 'compliant' },
  { domain: 'Security', metric: 'Threat modeling', target: 'Quarterly', status: 'scheduled' },
  { domain: 'UX', metric: 'WCAG 2.2 AA', target: '100% pages', status: 'compliant' },
  { domain: 'UX', metric: 'Design system adoption', target: '>90%', status: 'on_track' },
];

export const DEMO_COMPLIANCE: ComplianceEvolutionRow[] = [
  { category: 'Documentation', item: 'Architecture docs current', status: 'compliant', progress: 95 },
  { category: 'Technical Debt', item: 'Registered debt items', status: '42 open', progress: 68 },
  { category: 'Compliance', item: 'Architecture review pass rate', status: '98%', progress: 98 },
  { category: 'Compliance', item: 'CI policy checks', status: 'passing', progress: 100 },
  { category: 'Future', item: 'Technology radar Q3', status: 'published', progress: 100 },
  { category: 'Future', item: 'Multi-marketplace research', status: 'in_progress', progress: 45 },
];

export function buildGovernanceDashboard() {
  return {
    board_roles: BOARD_ROLES.length,
    architecture_principles: ARCHITECTURE_PRINCIPLES.length,
    technology_domains: TECHNOLOGY_DOMAINS.length,
    integration_standards: INTEGRATION_STANDARDS.length,
    compliance_areas: COMPLIANCE_REVIEW.length,
  };
}
